import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Button from "../../globalComponents/Button";
import { workTypeDesignTokens as tokens } from "../work-type-design-tokens";
import {
  getSchedulePresetRowIds,
  getScheduleProductivitySummary,
  ScheduleProductivitySummary
} from "./flexible-schedule-editor.selectors";

export type FlexibleScheduleMode = "DAILY_DATE" | "WEEKLY_PATTERN";

export interface FlexibleScheduleRow {
  id: string;
  label: string;
  displayLabel?: string;
  isWeekend?: boolean;
  startTime: string;
  workMinutes: number;
  isDayOff: boolean;
  waiveBreakAtFourHours: boolean;
  selected?: boolean;
  weekday?: number;
}

export interface FlexibleScheduleEditorProps {
  mode: FlexibleScheduleMode;
  value: FlexibleScheduleRow[];
  onChange: (value: FlexibleScheduleRow[]) => void;
  readOnly?: boolean;
  presentation?: "compact" | "productivity";
  targetMinutes?: number | null;
  sectionGuide?: string;
  getRowPreview?: (row: FlexibleScheduleRow) => {
    automaticBreakMinutes: number;
    expectedEnd: { time: string; crossesDay: boolean } | null;
  } | null;
}

const Editor = styled.section`
  display: grid;
  gap: 10px;
  min-width: 0;
`;

const EditorHeader = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Mode = styled.strong`
  color: ${props => props.theme.colors.midNightBlue};
`;

const ScheduleTable = styled.div`
  display: grid;
  gap: 6px;
  min-width: 0;
`;

const ScheduleRow = styled.fieldset`
  display: grid;
  grid-template-columns: minmax(90px, 1fr) minmax(100px, 1fr) minmax(120px, 1fr) minmax(90px, 1fr);
  gap: 8px;
  align-items: center;
  min-width: 0;
  margin: 0;
  border: 1px solid ${props => props.theme.colors.frameGrey};
  border-radius: 4px;
  padding: 8px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Legend = styled.legend`
  padding: 0 4px;
  font-weight: 700;
`;

const Field = styled.label`
  display: grid;
  gap: 4px;
  min-width: 0;
  color: ${props => props.theme.colors.darkGrey};

  input {
    min-width: 0;
  }
`;

const CheckField = styled.label`
  display: flex;
  gap: 6px;
  align-items: center;
  color: ${props => props.theme.colors.black};
`;

const ErrorText = styled.p`
  grid-column: 1 / -1;
  margin: 0;
  color: ${props => props.theme.colors.darkRed};
  font-weight: 700;
`;

const DayOffText = styled.strong`
  grid-column: 2 / -1;
  color: ${props => props.theme.colors.midNightBlue};
`;

const PreviewText = styled.p`
  grid-column: 1 / -1;
  margin: 0;
  color: ${props => props.theme.colors.darkGrey};
  font-size: 12px;
`;

const ProductivityRoot = styled.section`
  display: grid;
  gap: ${tokens.spacing.md};
  min-width: 0;
  color: ${tokens.color.text};
  font-size: 14px;
`;

const ProductivityHeader = styled.header`
  display: grid;
  gap: ${tokens.spacing.sm};

  h2,
  p {
    margin: 0;
  }

  h2 {
    font-size: 20px;
    line-height: 1.3;
  }

  p {
    color: ${tokens.color.textMuted};
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${tokens.spacing.sm};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  display: grid;
  gap: ${tokens.spacing.xs};
  min-width: 0;
  padding: 12px ${tokens.spacing.md};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.card};
  background: ${tokens.color.surface};

  span {
    color: ${tokens.color.textMuted};
    font-size: 12px;
    font-weight: 700;
  }

  strong {
    font-size: 18px;
  }
`;

const SummaryState = styled.strong<{ state: ScheduleProductivitySummary["state"] }>`
  width: fit-content;
  padding: 4px 10px;
  border: 1px solid
    ${props =>
      props.state === "over"
        ? "#FED7AA"
        : props.state === "met"
        ? tokens.tone.active.border
        : props.state === "missing"
        ? tokens.tone.neutral.border
        : tokens.tone.selected.border};
  border-radius: ${tokens.radius.pill};
  background: ${props =>
    props.state === "over"
      ? "#FFF7ED"
      : props.state === "met"
      ? tokens.tone.active.background
      : props.state === "missing"
      ? tokens.tone.neutral.background
      : tokens.tone.selected.background};
  color: ${props =>
    props.state === "over"
      ? "#9A3412"
      : props.state === "met"
      ? tokens.tone.active.text
      : tokens.color.textMuted};
  font-size: 12px !important;
`;

const BatchPanel = styled.section`
  display: grid;
  gap: 12px;
  padding: ${tokens.spacing.md};
  border: 1px solid ${tokens.tone.selected.border};
  border-radius: ${tokens.radius.card};
  background: ${tokens.tone.selected.background};

  h3,
  p {
    margin: 0;
  }

  h3 {
    font-size: 16px;
  }
`;

const BatchFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(160px, 220px)) minmax(220px, 1fr);
  gap: ${tokens.spacing.sm};
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ProductivityField = styled.label`
  display: grid;
  gap: ${tokens.spacing.xs};
  color: ${tokens.color.textMuted};
  font-size: 12px;
  font-weight: 700;

  input {
    min-width: 0;
    height: ${tokens.control.compactHeight};
    box-sizing: border-box;
    border: 1px solid ${tokens.color.borderStrong};
    border-radius: ${tokens.radius.control};
    padding: 0 12px;
    background: ${tokens.color.surface};
    color: ${tokens.color.text};

    &:focus-visible {
      outline: ${tokens.focus.outline};
      outline-offset: ${tokens.focus.offset};
    }
  }
`;

const DraftPreview = styled.div`
  display: grid;
  gap: ${tokens.spacing.xs};
  min-height: ${tokens.control.compactHeight};
  align-content: center;
  color: ${tokens.color.textMuted};
  font-size: 12px;

  strong {
    color: ${tokens.color.text};
  }
`;

const ActionGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${tokens.spacing.sm};
  align-items: center;
`;

const ProductivityButton = styled.button<{ primary?: boolean }>`
  min-height: 38px;
  border: 1px solid
    ${props => (props.primary ? tokens.color.focus : tokens.color.borderStrong)};
  border-radius: ${tokens.radius.control};
  padding: 0 12px;
  background: ${props => (props.primary ? tokens.color.focus : tokens.color.surface)};
  color: ${props => (props.primary ? "#FFFFFF" : tokens.color.text)};
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  &:focus-visible {
    outline: ${tokens.focus.outline};
    outline-offset: ${tokens.focus.offset};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.48;
  }
`;

const SelectionCount = styled.strong`
  padding: 4px 10px;
  border-radius: ${tokens.radius.pill};
  background: ${tokens.color.surface};
  color: ${tokens.color.text};
  font-size: 12px;
`;

const ActionReason = styled.p<{ error?: boolean }>`
  color: ${props => (props.error ? "#B45309" : tokens.color.textMuted)};
  font-size: 12px;
  font-weight: ${props => (props.error ? 700 : 400)};
`;

const ScheduleViewport = styled.div<{ $readOnly?: boolean }>`
  min-width: 0;
  max-height: ${props => props.$readOnly ? "none" : "560px"};
  overflow-x: auto;
  overflow-y: ${props => props.$readOnly ? "visible" : "auto"};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.card};
  background: ${tokens.color.surface};

  &:focus-visible {
    outline: ${tokens.focus.outline};
    outline-offset: ${tokens.focus.offset};
  }
`;

const ProductivityTable = styled.div`
  display: grid;
  width: 100%;
  min-width: 740px;
  box-sizing: border-box;
`;

const ProductivityTableRow = styled.div<{ header?: boolean; selected?: boolean }>`
  display: grid;
  grid-template-columns: 36px 100px 132px 72px 80px 86px minmax(160px, 1fr);
  gap: 6px;
  align-items: center;
  min-height: ${props => (props.header ? "44px" : "64px")};
  padding: 8px 10px;
  border-bottom: 1px solid ${tokens.color.border};
  background: ${props =>
    props.header
      ? tokens.color.surfaceMuted
      : props.selected
      ? tokens.tone.selected.background
      : tokens.color.surface};

  ${props =>
    props.header
      ? `
    position: sticky;
    top: 0;
    z-index: 2;
    color: ${tokens.color.textMuted};
    font-size: 12px;
    font-weight: 800;
  `
      : ""}

  &:last-child {
    border-bottom: 0;
  }

  > * {
    min-width: 0;
  }
`;

const RowInput = styled.input`
  width: 100%;
  min-width: 0;
  height: 38px;
  box-sizing: border-box;
  border: 1px solid ${tokens.color.borderStrong};
  border-radius: 8px;
  padding: 0 9px;
  color: ${tokens.color.text};

  &:focus-visible {
    outline: ${tokens.focus.outline};
    outline-offset: 1px;
  }

  &:disabled {
    background: ${tokens.color.surfaceMuted};
    color: ${tokens.color.textMuted};
  }
`;

const ProductivityDate = styled.strong`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  line-height: 1.15;
`;

const WeekendBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 16px;
  border: 1px solid ${tokens.tone.selected.border};
  border-radius: ${tokens.radius.pill};
  padding: 0 6px;
  background: ${tokens.tone.selected.background};
  color: ${tokens.tone.selected.text};
  font-size: 9px;
  font-weight: 800;
`;

const RowStatus = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${tokens.spacing.xs};
  align-items: center;
`;

const StatusChip = styled.span<{ tone?: "active" | "danger" | "neutral" | "selected" }>`
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  border: 1px solid
    ${props => tokens.tone[props.tone ?? "neutral"].border};
  border-radius: ${tokens.radius.pill};
  padding: 0 9px;
  background: ${props => tokens.tone[props.tone ?? "neutral"].background};
  color: ${props => tokens.tone[props.tone ?? "neutral"].text};
  font-size: 11px;
  font-weight: 800;
`;

const RowAction = styled.button`
  min-height: 30px;
  border: 1px solid ${tokens.color.borderStrong};
  border-radius: 6px;
  padding: 3px 8px;
  background: ${tokens.color.surface};
  color: ${tokens.color.text};
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.14);
  transition: border-color 140ms ease, background 140ms ease, color 140ms ease,
    box-shadow 140ms ease, transform 80ms ease;

  &:hover:not(:disabled) {
    border-color: ${tokens.color.focus};
    background: ${tokens.tone.selected.background};
    color: ${tokens.color.focus};
    box-shadow: 0 2px 5px rgba(15, 23, 42, 0.18);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow: 0 0 1px rgba(15, 23, 42, 0.18);
  }

  &:focus-visible {
    outline: ${tokens.focus.outline};
    outline-offset: 1px;
  }

  &:disabled {
    cursor: not-allowed;
    box-shadow: none;
    background: ${tokens.color.surfaceMuted};
    opacity: 0.52;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ValidationSummary = styled.div`
  padding: 10px 12px;
  border: 1px solid ${tokens.tone.danger.border};
  border-radius: ${tokens.radius.control};
  background: ${tokens.tone.danger.background};
  color: ${tokens.tone.danger.text};
  font-weight: 700;
`;

const LiveRegion = styled.p`
  min-height: 18px;
  margin: 0;
  color: ${tokens.color.textMuted};
  font-weight: 700;
`;

function getRowError(row: FlexibleScheduleRow) {
  if (row.isDayOff) return null;
  if (!row.startTime) return "출근 시간을 입력하세요.";
  if (row.workMinutes <= 0 || row.workMinutes > 720) {
    return "계획 근로시간은 1분 이상 12시간 이하여야 합니다.";
  }

  const [hour, minute] = row.startTime.split(":").map(Number);
  if (hour * 60 + minute + row.workMinutes >= 24 * 60) {
    return "종료 시간이 다음 날입니다. 출근 시간과 계획 근로시간을 확인하세요.";
  }
  return null;
}

function formatMinutes(minutes: number) {
  const absolute = Math.abs(minutes);
  const hours = Math.floor(absolute / 60);
  const remainder = absolute % 60;
  if (hours === 0) return `${remainder}분`;
  return remainder === 0 ? `${hours}시간` : `${hours}시간 ${remainder}분`;
}

function allocationStateText(summary: ScheduleProductivitySummary) {
  if (summary.state === "missing") {
    return "적용기간과 기준시간을 입력하면 목표가 계산됩니다.";
  }
  if (summary.state === "met") return "✓ 목표 충족";
  if (summary.state === "under") {
    return `↓ ${formatMinutes(Math.abs(summary.deltaMinutes ?? 0))} 남음`;
  }
  return `↑ ${formatMinutes(summary.deltaMinutes ?? 0)} 초과`;
}

interface ProductivityScheduleEditorProps
  extends Omit<FlexibleScheduleEditorProps, "presentation"> {}

const ProductivityScheduleEditor = ({
  mode,
  value,
  onChange,
  readOnly = false,
  targetMinutes,
  sectionGuide,
  getRowPreview
}: ProductivityScheduleEditorProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(value.filter(row => row.selected).map(row => row.id))
  );
  const [draftStartTime, setDraftStartTime] = useState("09:00");
  const [draftWorkHours, setDraftWorkHours] = useState("8");
  const [announcement, setAnnouncement] = useState("");
  const draftRef = useRef({ draftStartTime, draftWorkHours });
  draftRef.current = { draftStartTime, draftWorkHours };
  const rowKeySignature = value.map(row => row.id).join("\u0000");

  useEffect(() => {
    const currentIds = new Set(value.map(row => row.id));
    setSelectedIds(previous =>
      new Set(Array.from(previous).filter(id => currentIds.has(id)))
    );
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(draftRef.current.draftStartTime)) {
      setDraftStartTime("09:00");
    }
    const workHours = Number(draftRef.current.draftWorkHours);
    if (!Number.isFinite(workHours) || workHours < 0.5 || workHours > 12) {
      setDraftWorkHours("8");
    }
    setAnnouncement("");
    // row key changes are the external reset boundary; value edits keep local selection/draft.
  }, [rowKeySignature]);

  const summary = useMemo(
    () => getScheduleProductivitySummary({ mode, rows: value, targetMinutes }),
    [mode, targetMinutes, value]
  );
  const selectedRows = value.filter(row => selectedIds.has(row.id));
  const draftMinutes = Number(draftWorkHours) * 60;
  const draftError =
    !Number.isFinite(draftMinutes) || draftMinutes < 30 || draftMinutes > 720
      ? "계획근로시간은 0.5시간 이상 12시간 이하로 입력하세요."
      : !/^([01]\d|2[0-3]):[0-5]\d$/.test(draftStartTime)
      ? "유효한 출근시간을 입력하세요."
      : null;
  const draftPreview =
    !draftError && getRowPreview
      ? getRowPreview({
          id: "batch-draft",
          label: "일괄 입력",
          startTime: draftStartTime,
          workMinutes: Math.round(draftMinutes),
          isDayOff: false,
          waiveBreakAtFourHours: false
        })
      : null;
  const canApplyWaiver =
    selectedRows.length > 0 &&
    selectedRows.every(row => !row.isDayOff && row.workMinutes === 240);
  const rowAnalyses = value.map(row => {
    const preview = getRowPreview?.(row) ?? null;
    const error =
      getRowError(row) ??
      (preview?.expectedEnd?.crossesDay
        ? "예상 종료 시간이 다음 날입니다. 출근·근로·휴게시간을 확인하세요."
        : null);
    return { row, preview, error };
  });
  const invalidCount = rowAnalyses.filter(item => item.error).length;

  const selectPreset = (preset: "weekday" | "weekend" | "all") => {
    setSelectedIds(new Set(getSchedulePresetRowIds(value, mode, preset)));
    setAnnouncement("");
  };

  const applyToSelection = (
    resultMessage: string,
    update: (row: FlexibleScheduleRow) => FlexibleScheduleRow
  ) => {
    if (selectedRows.length === 0) return;
    onChange(value.map(row => (selectedIds.has(row.id) ? update(row) : row)));
    setAnnouncement(`선택한 ${selectedRows.length}개 일정에 ${resultMessage}`);
  };

  return (
    <ProductivityRoot aria-label="탄력 일정 생산성 workspace">
      <ProductivityHeader>
        <h2>{mode === "DAILY_DATE" ? "날짜별 일정 배분" : "주간 패턴 배분"}</h2>
        {sectionGuide && <p>{sectionGuide}</p>}
      </ProductivityHeader>

      <SummaryGrid aria-label="일정 배분 요약">
        <SummaryCard>
          <span>{mode === "DAILY_DATE" ? "기간 목표" : "주간 목표"}</span>
          <strong>
            {summary.targetMinutes === null
              ? "계산 대기"
              : formatMinutes(summary.targetMinutes)}
          </strong>
        </SummaryCard>
        <SummaryCard>
          <span>현재 배분</span>
          <strong>{formatMinutes(summary.currentMinutes)}</strong>
        </SummaryCard>
        <SummaryCard>
          <span>배분 상태</span>
          <SummaryState state={summary.state}>
            {allocationStateText(summary)}
          </SummaryState>
        </SummaryCard>
      </SummaryGrid>

      {!readOnly && (
        <BatchPanel aria-label="일정 일괄 입력">
          <div>
            <h3>일괄 입력</h3>
            <p>현재 일정과 분리된 초안을 선택한 행에만 명시적으로 적용합니다.</p>
          </div>
          <BatchFields>
            <ProductivityField>
              일괄 출근시간
              <input
                type="time"
                value={draftStartTime}
                onChange={event => setDraftStartTime(event.currentTarget.value)}
              />
            </ProductivityField>
            <ProductivityField>
              일괄 계획근로시간 (시간)
              <input
                type="number"
                min="0.5"
                max="12"
                step="0.5"
                value={draftWorkHours}
                onChange={event => setDraftWorkHours(event.currentTarget.value)}
              />
            </ProductivityField>
            <DraftPreview aria-label="일괄 입력 자동 계산">
              <span>자동 휴게 · 예상 퇴근</span>
              <strong>
                {draftPreview?.expectedEnd
                  ? `${draftPreview.automaticBreakMinutes}분 · ${
                      draftPreview.expectedEnd.crossesDay ? "다음 날 " : ""
                    }${draftPreview.expectedEnd.time}`
                  : "유효한 초안을 입력하세요."}
              </strong>
            </DraftPreview>
          </BatchFields>
          <ActionGroup aria-label="빠른 일정 선택">
            <ProductivityButton type="button" onClick={() => selectPreset("weekday")}>
              평일
            </ProductivityButton>
            <ProductivityButton type="button" onClick={() => selectPreset("weekend")}>
              주말
            </ProductivityButton>
            <ProductivityButton type="button" onClick={() => selectPreset("all")}>
              전체
            </ProductivityButton>
            <ProductivityButton
              type="button"
              onClick={() => {
                setSelectedIds(new Set());
                setAnnouncement("");
              }}
            >
              선택 해제
            </ProductivityButton>
            <SelectionCount>{selectedRows.length}개 선택</SelectionCount>
          </ActionGroup>
          <ActionGroup aria-label="선택 일정 작업">
            <ProductivityButton
              primary
              type="button"
              disabled={selectedRows.length === 0 || Boolean(draftError)}
              onClick={() =>
                applyToSelection("적용했습니다.", row => ({
                  ...row,
                  startTime: draftStartTime,
                  workMinutes: Math.round(draftMinutes),
                  isDayOff: false,
                  waiveBreakAtFourHours:
                    draftMinutes === 240 ? row.waiveBreakAtFourHours : false
                }))
              }
            >
              선택한 일정에 적용
            </ProductivityButton>
            <ProductivityButton
              type="button"
              disabled={selectedRows.length === 0}
              onClick={() =>
                applyToSelection("휴무를 적용했습니다.", row => ({
                  ...row,
                  isDayOff: true,
                  waiveBreakAtFourHours: false
                }))
              }
            >
              휴무 적용
            </ProductivityButton>
            <ProductivityButton
              type="button"
              disabled={selectedRows.length === 0}
              onClick={() =>
                applyToSelection("근무일 변경을 적용했습니다.", row => ({
                  ...row,
                  isDayOff: false,
                  startTime: row.startTime || draftStartTime || "09:00",
                  workMinutes:
                    row.workMinutes > 0 ? row.workMinutes : Math.round(draftMinutes) || 480
                }))
              }
            >
              근무일로 변경
            </ProductivityButton>
            <ProductivityButton
              type="button"
              disabled={!canApplyWaiver}
              onClick={() =>
                applyToSelection("휴게 미사용을 적용했습니다.", row => ({
                  ...row,
                  waiveBreakAtFourHours: true
                }))
              }
            >
              4시간 · 휴게 미사용 적용
            </ProductivityButton>
            <ProductivityButton
              type="button"
              disabled={!canApplyWaiver}
              onClick={() =>
                applyToSelection("휴게 미사용을 해제했습니다.", row => ({
                  ...row,
                  waiveBreakAtFourHours: false
                }))
              }
            >
              4시간 · 휴게 미사용 해제
            </ProductivityButton>
          </ActionGroup>
          {draftError ? (
            <ActionReason error>{draftError}</ActionReason>
          ) : selectedRows.length === 0 ? (
            <ActionReason>먼저 적용할 일정을 선택하세요.</ActionReason>
          ) : !canApplyWaiver ? (
            <ActionReason error>
              휴게 미사용은 선택한 모든 일정이 정확히 4시간 근무여야 적용할 수 있습니다.
            </ActionReason>
          ) : (
            <ActionReason>
              {selectedRows.length}개 일정에 동일한 작업을 적용할 준비가 됐습니다.
            </ActionReason>
          )}
          <LiveRegion role="status" aria-live="polite">
            {announcement}
          </LiveRegion>
        </BatchPanel>
      )}

      {invalidCount > 0 && (
        <ValidationSummary role="alert">
          확인이 필요한 일정 {invalidCount}개가 있습니다. 아래 행 상태를 확인하세요.
        </ValidationSummary>
      )}

      <ScheduleViewport
        $readOnly={readOnly}
        role="region"
        aria-label="탄력 일정 표"
        tabIndex={0}
      >
        <ProductivityTable role="table" aria-label="탄력 일정 행">
          <ProductivityTableRow role="row" header>
            <span role="columnheader">선택</span>
            <span role="columnheader">
              {mode === "WEEKLY_PATTERN" ? "요일" : "날짜/요일"}
            </span>
            <span role="columnheader">출근</span>
            <span role="columnheader">계획근로</span>
            <span role="columnheader">자동 휴게</span>
            <span role="columnheader">예상 퇴근</span>
            <span role="columnheader">상태</span>
          </ProductivityTableRow>
          {rowAnalyses.map(({ row, preview, error }) => {
            const selected = selectedIds.has(row.id);
            const errorId = `${row.id}-productivity-error`;
            return (
              <ProductivityTableRow key={row.id} role="row" selected={selected}>
                <span role="cell">
                  {!readOnly && (
                    <input
                      type="checkbox"
                      aria-label={`${row.label} 선택`}
                      checked={selected}
                      onChange={event => {
                        const checked = event.currentTarget.checked;
                        setSelectedIds(previous => {
                          const next = new Set(previous);
                          checked ? next.add(row.id) : next.delete(row.id);
                          return next;
                        });
                        setAnnouncement("");
                      }}
                    />
                  )}
                </span>
                <ProductivityDate
                  role="rowheader"
                  aria-label={
                    row.isWeekend
                      ? `${row.displayLabel ?? row.label} 주말`
                      : undefined
                  }
                >
                  <span>{row.displayLabel ?? row.label}</span>
                  {row.isWeekend && <WeekendBadge aria-hidden="true">주말</WeekendBadge>}
                </ProductivityDate>
                <span role="cell">
                  <RowInput
                    type="time"
                    aria-label={`${row.label} 출근`}
                    disabled={readOnly || row.isDayOff}
                    value={row.isDayOff ? "" : row.startTime}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? errorId : undefined}
                    onChange={event =>
                      onChange(
                        value.map(current =>
                          current.id === row.id
                            ? { ...current, startTime: event.currentTarget.value }
                            : current
                        )
                      )
                    }
                  />
                </span>
                <span role="cell">
                  <RowInput
                    type="number"
                    min="0.5"
                    max="12"
                    step="0.5"
                    aria-label={`${row.label} 계획근로시간`}
                    disabled={readOnly || row.isDayOff}
                    value={row.isDayOff ? "" : row.workMinutes / 60}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? errorId : undefined}
                    onChange={event => {
                      const workMinutes = Math.round(
                        Number(event.currentTarget.value) * 60
                      );
                      onChange(
                        value.map(current =>
                          current.id === row.id
                            ? {
                                ...current,
                                workMinutes,
                                waiveBreakAtFourHours:
                                  workMinutes === 240
                                    ? current.waiveBreakAtFourHours
                                    : false
                              }
                            : current
                        )
                      );
                    }}
                  />
                </span>
                <span role="cell">
                  {row.isDayOff ? "—" : preview ? `${preview.automaticBreakMinutes}분` : "—"}
                </span>
                <span role="cell">
                  {row.isDayOff
                    ? "—"
                    : preview?.expectedEnd
                    ? `${preview.expectedEnd.crossesDay ? "다음 날 " : ""}${
                        preview.expectedEnd.time
                      }`
                    : "—"}
                </span>
                <RowStatus role="cell">
                  <StatusChip tone={error ? "danger" : row.isDayOff ? "neutral" : "active"}>
                    {error ? "⚠ 확인 필요" : row.isDayOff ? "휴무" : "입력"}
                  </StatusChip>
                  {row.waiveBreakAtFourHours && (
                    <StatusChip tone="selected">휴게 미사용</StatusChip>
                  )}
                  {!readOnly && (
                    <>
                      <RowAction
                        type="button"
                        aria-label={`${row.label} ${
                          row.isDayOff ? "근무일로 변경" : "휴무 적용"
                        }`}
                        onClick={() =>
                          onChange(
                            value.map(current =>
                              current.id === row.id
                                ? {
                                    ...current,
                                    isDayOff: !current.isDayOff,
                                    startTime:
                                      current.isDayOff && !current.startTime
                                        ? "09:00"
                                        : current.startTime,
                                    workMinutes:
                                      current.isDayOff && current.workMinutes <= 0
                                        ? 480
                                        : current.workMinutes,
                                    waiveBreakAtFourHours: false
                                  }
                                : current
                            )
                          )
                        }
                      >
                        {row.isDayOff ? "근무일로 변경" : "휴무 적용"}
                      </RowAction>
                      <RowAction
                        type="button"
                        disabled={row.isDayOff || row.workMinutes !== 240}
                        aria-label={`${row.label} 휴게 미사용 ${
                          row.waiveBreakAtFourHours ? "해제" : "적용"
                        }`}
                        onClick={() =>
                          onChange(
                            value.map(current =>
                              current.id === row.id
                                ? {
                                    ...current,
                                    waiveBreakAtFourHours:
                                      !current.waiveBreakAtFourHours
                                  }
                                : current
                            )
                          )
                        }
                      >
                        {row.waiveBreakAtFourHours
                          ? "휴게 미사용 해제"
                          : "휴게 미사용 적용"}
                      </RowAction>
                    </>
                  )}
                  {error && <span id={errorId}>{error}</span>}
                </RowStatus>
              </ProductivityTableRow>
            );
          })}
        </ProductivityTable>
      </ScheduleViewport>
    </ProductivityRoot>
  );
};

export const FlexibleScheduleEditor = ({
  mode,
  value,
  onChange,
  readOnly = false,
  presentation = "compact",
  targetMinutes,
  sectionGuide,
  getRowPreview
}: FlexibleScheduleEditorProps) => {
  if (presentation === "productivity") {
    return (
      <ProductivityScheduleEditor
        mode={mode}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        targetMinutes={targetMinutes}
        sectionGuide={sectionGuide}
        getRowPreview={getRowPreview}
      />
    );
  }

  const updateRow = (id: string, patch: Partial<FlexibleScheduleRow>) => {
    onChange(value.map(row => (row.id === id ? { ...row, ...patch } : row)));
  };
  const selectedRows = value.filter(row => row.selected && !row.isDayOff);

  return (
    <Editor aria-label="탄력 일정 편집기">
      <EditorHeader>
        <Mode>
          {mode === "DAILY_DATE" ? "날짜별 스케줄" : "주간 반복"}
        </Mode>
        {!readOnly && (
          <Button
            type="button"
            disabled={selectedRows.length < 2}
            onClick={() => {
              const source = selectedRows[0];
              if (!source) return;
              onChange(
                value.map(row =>
                  row.selected
                    ? {
                        ...row,
                        startTime: source.startTime,
                        workMinutes: source.workMinutes,
                        waiveBreakAtFourHours: source.waiveBreakAtFourHours
                      }
                    : row
                )
              );
            }}
          >
            선택 일정 일괄 적용
          </Button>
        )}
      </EditorHeader>
      <ScheduleTable role="group" aria-label="일정 행">
        {value.map(row => {
          const preview = getRowPreview?.(row) ?? null;
          const error =
            getRowError(row) ??
            (preview?.expectedEnd?.crossesDay
              ? "예상 종료 시간이 다음 날입니다. 출근·근로·휴게시간을 확인하세요."
              : null);
          const errorId = `${row.id}-schedule-error`;
          return (
            <ScheduleRow key={row.id} disabled={readOnly}>
              <Legend>{row.label}</Legend>
              {!readOnly && (
                <CheckField>
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={Boolean(row.selected)}
                    onChange={event =>
                      updateRow(row.id, { selected: event.currentTarget.checked })
                    }
                  />
                  일괄 적용 선택
                </CheckField>
              )}
              <CheckField>
                <input
                  type="checkbox"
                  disabled={readOnly}
                  checked={row.isDayOff}
                  onChange={event =>
                    updateRow(row.id, { isDayOff: event.currentTarget.checked })
                  }
                />
                {row.label} 휴무
              </CheckField>
              {row.isDayOff ? (
                <DayOffText>휴무</DayOffText>
              ) : (
                <>
                  <Field>
                    출근 시간
                    <input
                      type="time"
                      disabled={readOnly}
                      value={row.startTime}
                      aria-describedby={error ? errorId : undefined}
                      aria-invalid={Boolean(error)}
                      onChange={event =>
                        updateRow(row.id, { startTime: event.currentTarget.value })
                      }
                    />
                  </Field>
                  <Field>
                    계획 근로시간 (분)
                    <input
                      type="number"
                      disabled={readOnly}
                      min={1}
                      max={720}
                      value={row.workMinutes}
                      aria-describedby={error ? errorId : undefined}
                      aria-invalid={Boolean(error)}
                      onChange={event =>
                        updateRow(row.id, {
                          workMinutes: Number(event.currentTarget.value)
                        })
                      }
                    />
                  </Field>
                  {row.workMinutes === 240 && (
                    <CheckField>
                      <input
                        type="checkbox"
                        disabled={readOnly}
                        checked={row.waiveBreakAtFourHours}
                        onChange={event =>
                          updateRow(row.id, {
                            waiveBreakAtFourHours: event.currentTarget.checked
                          })
                        }
                      />
                      정확히 4시간 근무 시 휴게 미사용
                    </CheckField>
                  )}
                </>
              )}
              {preview && preview.expectedEnd && (
                <PreviewText>
                  자동 휴게 {preview.automaticBreakMinutes}분 · 예상 퇴근 {preview.expectedEnd.crossesDay ? "다음 날 " : ""}
                  {preview.expectedEnd.time}
                </PreviewText>
              )}
              {error && (
                <ErrorText id={errorId} role="alert">
                  {row.label}: {error}
                </ErrorText>
              )}
            </ScheduleRow>
          );
        })}
      </ScheduleTable>
    </Editor>
  );
};
